import MicIcon from 'assets/icons/ic_mic_none.svg?react';
import MicOffIcon from 'assets/icons/ic_mic_off.svg?react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconWarningAmber,
  Loader,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import styles from './StepDeviceSetup.module.scss';

export type MicCheckStatus = 'listening' | 'noSound' | 'working';

export interface MicCheckState {
  status: MicCheckStatus;
  elapsedMs: number;
  aboveTargetMs: number;
}

/** Where the target line sits on the bar, in percent of full scale. */
export const MIC_CHECK_TARGET = 60;
/**
 * Total time the level must spend past the line. Cumulative rather than
 * continuous, so the gaps between words don't reset it, while a single cough
 * or desk knock (a few frames) still isn't enough.
 */
export const MIC_CHECK_HOLD_MS = 300;
/**
 * How long we listen before telling the candidate we can't hear them. Counted
 * from when the stream arrives, so it has to cover reading the instruction
 * and the sentence too, or the warning flashes mid-sentence.
 */
export const MIC_CHECK_TIMEOUT_MS = 10000;

export const INITIAL_MIC_CHECK: MicCheckState = {
  status: 'listening',
  elapsedMs: 0,
  aboveTargetMs: 0,
};

/**
 * Advances the check by one meter frame. Once the candidate passes the line
 * the check stays passed, even if the bar later drops back under it.
 */
export function advanceMicCheck(
  state: MicCheckState,
  level: number,
  deltaMs: number
): MicCheckState {
  if (state.status === 'working') return state;

  const elapsedMs = state.elapsedMs + deltaMs;
  const aboveTargetMs =
    level >= MIC_CHECK_TARGET ? state.aboveTargetMs + deltaMs : state.aboveTargetMs;

  if (aboveTargetMs >= MIC_CHECK_HOLD_MS) {
    return { status: 'working', elapsedMs, aboveTargetMs };
  }
  return {
    status: elapsedMs >= MIC_CHECK_TIMEOUT_MS ? 'noSound' : 'listening',
    elapsedMs,
    aboveTargetMs,
  };
}

// The bar follows loudness on a decibel scale, like any audio meter. A linear
// scale leaves quiet laptop mics (speech around -35 dBFS) near zero, so the
// bar looked dead for many candidates.
const METER_FLOOR_DB = -60;
const METER_CEILING_DB = -10;
// Time constants, not per-frame factors, so the bar moves the same on 60 Hz
// and 120 Hz screens. Fast attack so a word registers at once; slower release
// so the bar settles between words instead of flickering.
const METER_ATTACK_MS = 25;
const METER_RELEASE_MS = 150;
// A hidden tab pauses animation frames. Cap the gap so returning to the tab
// doesn't count the whole absence towards the "No sound" timeout.
const MAX_FRAME_DELTA_MS = 100;

/** Maps signal RMS (0..1) to a bar level in percent. */
export function rmsToMeterLevel(rms: number): number {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  const level =
    ((db - METER_FLOOR_DB) / (METER_CEILING_DB - METER_FLOOR_DB)) * 100;
  return Math.min(100, Math.max(0, level));
}

/** Moves the displayed level towards the target, frame-rate independent. */
export function smoothMeterLevel(
  current: number,
  target: number,
  deltaMs: number
): number {
  const timeConstant = target > current ? METER_ATTACK_MS : METER_RELEASE_MS;
  const alpha = 1 - Math.exp(-deltaMs / timeConstant);
  return current + (target - current) * alpha;
}

interface MicrophoneCheckProps {
  /** null until the candidate grants microphone access. */
  stream: MediaStream | null;
  onWorkingChange: (isWorking: boolean) => void;
  /** Re-acquires the stream, so a newly selected or unmuted mic is picked up. */
  onRetry: () => void;
}

export function MicrophoneCheck({
  stream,
  onWorkingChange,
  onRetry,
}: MicrophoneCheckProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<MicCheckStatus>('listening');
  const checkRef = useRef<MicCheckState>(INITIAL_MIC_CHECK);
  // The bar is written straight to the DOM every frame. Routing 60 updates a
  // second through React state, plus a CSS width transition that restarted on
  // each one, is what made the old bar lag and stutter.
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onWorkingChange(status === 'working');
  }, [status, onWorkingChange]);

  useEffect(() => {
    // Every new stream (first grant or "Try again") starts a fresh check.
    checkRef.current = INITIAL_MIC_CHECK;
    setStatus('listening');
    if (fillRef.current) fillRef.current.style.width = '0%';

    const audioTrack = stream?.getAudioTracks()[0];
    if (!stream || !audioTrack) return;

    const audioContext = new AudioContext();
    // The context can start suspended under the autoplay policy (Safari
    // always, Chrome after a reload on this step). A suspended context only
    // ever reads silence, which froze the bar at zero. resume() only works
    // inside a user gesture there, so also retry on the candidate's next tap
    // or key press.
    const resume = () => {
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
    };
    resume();
    audioContext.addEventListener('statechange', resume);
    const gestureEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
    gestureEvents.forEach(event =>
      window.addEventListener(event, resume, { passive: true })
    );

    // Analyse only the audio track. The stream also carries the camera track.
    const source = audioContext.createMediaStreamSource(
      new MediaStream([audioTrack])
    );
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    // Safari only pulls audio through nodes that reach the destination, so an
    // analyser left dangling reads zeros there. A muted gain node completes
    // the graph without playing the candidate's voice back to them.
    const mute = audioContext.createGain();
    mute.gain.value = 0;
    source.connect(analyser);
    analyser.connect(mute);
    mute.connect(audioContext.destination);

    // Float samples, not bytes: 8-bit samples round anything under about
    // -42 dBFS to silence, so quiet mics never moved the bar at all.
    const samples = new Float32Array(analyser.fftSize);
    let level = 0;
    let lastFrameTime: number | null = null;
    let animationFrame: number;

    const updateLevel = (frameTime: number) => {
      const deltaMs =
        lastFrameTime === null
          ? 0
          : Math.min(frameTime - lastFrameTime, MAX_FRAME_DELTA_MS);
      lastFrameTime = frameTime;

      analyser.getFloatTimeDomainData(samples);
      let sumOfSquares = 0;
      for (const sample of samples) {
        sumOfSquares += sample * sample;
      }
      const rms = Math.sqrt(sumOfSquares / samples.length);
      level = smoothMeterLevel(level, rmsToMeterLevel(rms), deltaMs);
      if (fillRef.current) {
        fillRef.current.style.width = `${level}%`;
      }

      const previousStatus = checkRef.current.status;
      checkRef.current = advanceMicCheck(checkRef.current, level, deltaMs);
      if (checkRef.current.status !== previousStatus) {
        setStatus(checkRef.current.status);
      }

      animationFrame = requestAnimationFrame(updateLevel);
    };

    animationFrame = requestAnimationFrame(updateLevel);

    return () => {
      cancelAnimationFrame(animationFrame);
      audioContext.removeEventListener('statechange', resume);
      gestureEvents.forEach(event => window.removeEventListener(event, resume));
      source.disconnect();
      analyser.disconnect();
      mute.disconnect();
      audioContext.close().catch(() => {});
    };
  }, [stream]);

  const isWorking = status === 'working';

  return (
    <div className={styles.deviceSection}>
      <div className={styles.deviceHeader}>
        <div className={styles.deviceIcon}>
          {stream ? (
            <MicIcon className={styles.micIcon} />
          ) : (
            <MicOffIcon className={styles.micIcon} />
          )}
        </div>
        <span className={styles.deviceTitle}>{t('aiInterview.microphone')}</span>
        {stream && <MicCheckStatusIndicator status={status} />}
      </div>

      {stream ? (
        <>
          <p className={styles.deviceHint}>
            {t(
              isWorking
                ? 'aiInterview.micCheck.heardClearly'
                : 'aiInterview.micCheck.instruction'
            )}
          </p>
          <p className={styles.micSentence}>{t('aiInterview.micCheck.sentence')}</p>
          <div className={styles.micBar}>
            <div className={styles.micBarBackground} />
            <div
              ref={fillRef}
              className={`${styles.micBarFill} ${isWorking ? styles.micBarFillWorking : ''}`}
            />
            <div
              className={styles.micBarTarget}
              style={{ left: `${MIC_CHECK_TARGET}%` }}
            />
          </div>
          {status === 'noSound' && (
            <div className={styles.micWarning} role="alert">
              <IconWarningAmber className={styles.micWarningIcon} />
              <div className={styles.micWarningContent}>
                <p className={styles.micWarningTitle}>
                  {t('aiInterview.micCheck.cantHearTitle')}
                </p>
                <p className={styles.micWarningDescription}>
                  {t('aiInterview.micCheck.cantHearDescription')}
                </p>
                <Button size="small" variant="secondary" onClick={onRetry}>
                  {t('aiInterview.micCheck.tryAgain')}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className={styles.deviceHint}>{t('aiInterview.pleaseAllowAccess')}</p>
      )}
    </div>
  );
}

function MicCheckStatusIndicator({ status }: { status: MicCheckStatus }) {
  const { t } = useTranslation();

  if (status === 'listening') {
    return (
      <span className={styles.micListening}>
        <Loader variant="spinner" size="small" />
        {t('aiInterview.micCheck.listening')}
      </span>
    );
  }

  const isWorking = status === 'working';
  return (
    <Tag
      isPresentational
      isDismissible={false}
      tagColor={isWorking ? TagColor.GREEN : TagColor.YELLOW}
      size={TagSize.SMALL}
    >
      {t(isWorking ? 'aiInterview.micCheck.working' : 'aiInterview.micCheck.noSound')}
    </Tag>
  );
}
